import { useMemo } from 'react';
import { Button, IconButton, HStack, Icon, Badge } from '@chakra-ui/react';
import { FiUsers, FiEdit, FiTrash, FiUpload, FiDownload } from 'react-icons/fi';
import type { ColumnDef } from '@tanstack/react-table';
import { PageContainer } from '@/client/components/layout/PageContainer';
import { LoadingSpinner } from '@/design-system/components/LoadingSpinner';
import { EmptyState } from '@/design-system/components/EmptyState';
import { DataTable } from '@/design-system/components/DataTable';
import { TeamDialog } from '../components/TeamDialog';
import { ConfirmDialog } from '@/design-system/components/ConfirmDialog';
import { ImportUsersDialog } from '@/components/Modal/ImportUsersDialog';
import { formatters } from '@/client/utils/formatters';
import type { TeamMember } from '@/lib/sdk/team/types';
import { useTeamPage } from '../hooks';

export function TeamPage() {
  const p = useTeamPage();

  const columns = useMemo<ColumnDef<TeamMember>[]>(
    () => [
      { accessorKey: 'name', header: 'Name' },
      {
        accessorKey: 'phone_number', header: 'Phone',
        cell: ({ row }) => formatters.phoneNumber(row.original.country_code, row.original.phone_number),
      },
      { accessorKey: 'nickname', header: 'Nickname' },
      {
        accessorKey: 'org_permissions', header: 'Permission',
        cell: ({ getValue }) => {
          const permission = getValue() as string;
          return <Badge colorScheme={permission === 'admin' ? 'purple' : 'blue'}>{permission === 'admin' ? 'Administrator' : 'User'}</Badge>;
        },
      },
      { accessorKey: 'created_on', header: 'Added', cell: ({ getValue }) => formatters.date(getValue() as string) },
      ...((p.canEditTeam || p.canDeleteTeam) ? [{
        id: 'actions', header: 'Actions',
        cell: ({ row }: { row: any }) => (
          <HStack spacing={2}>
            {p.canEditTeam && <IconButton aria-label="Edit member" icon={<Icon as={FiEdit} />} size="sm" variant="ghost" onClick={() => p.setEditingMember(row.original)} />}
            {p.canDeleteTeam && <IconButton aria-label="Delete member" icon={<Icon as={FiTrash} />} size="sm" variant="ghost" colorScheme="red" onClick={() => p.setDeletingMember(row.original)} />}
          </HStack>
        ),
      } as ColumnDef<TeamMember>] : []),
    ],
    [p.canEditTeam, p.canDeleteTeam, p.setEditingMember, p.setDeletingMember]
  );

  if (p.isLoading) return <LoadingSpinner message="Loading team members..." />;

  return (
    <>
      <PageContainer
        title="Team Members"
        description="View and manage your team members"
        breadcrumbs={p.breadcrumbItems}
        action={
          <HStack spacing={3}>
            {p.canImportTeam && (
              <Button
                leftIcon={<Icon as={FiUpload} />}
                variant="outline"
                colorScheme="brand"
                onClick={() => p.setIsImportDialogOpen(true)}
                size="lg"
                data-testid="team-import-btn"
              >
                Import
              </Button>
            )}
            <Button
              leftIcon={<Icon as={FiDownload} />}
              variant="outline"
              colorScheme="brand"
              onClick={p.handleExportUsers}
              size="lg"
              data-testid="team-export-btn"
            >
              Export
            </Button>
            {p.canAddTeam && (
              <Button leftIcon={<Icon as={FiUsers} />} colorScheme="brand" onClick={() => p.setIsAddDialogOpen(true)} size="lg" data-testid="team-add-btn">Add Team Member</Button>
            )}
          </HStack>
        }
      >
        {!p.members || p.members.length === 0 ? (
          <EmptyState
            icon={<Icon as={FiUsers} boxSize={12} />}
            title="No team members yet"
            description="Add your first team member to get started"
            action={p.canAddTeam ? { label: 'Add Team Member', onClick: () => p.setIsAddDialogOpen(true) } : undefined}
          />
        ) : (
          <DataTable data={p.members} columns={columns} enableSorting enableFiltering enablePagination pageSize={20} />
        )}
      </PageContainer>

      {p.canManageTeam && (
        <>
          <TeamDialog
            isOpen={p.isAddDialogOpen || !!p.editingMember}
            onClose={() => { p.setIsAddDialogOpen(false); p.setEditingMember(null); }}
            isCurrentUserAdmin={p.isCurrentUserAdmin}
            phoneLocked={p.editingMember ? p.isPhoneLocked(p.editingMember) : false}
            initialData={p.editingMember ? {
              country_code: p.editingMember.country_code,
              phone_number: p.editingMember.phone_number,
              name: p.editingMember.name,
              org_code: p.editingMember.org_code || '',
              nickname: p.editingMember.nickname || '',
              org_permissions: p.editingMember.org_permissions as any,
              feature_permissions: p.editingMember.feature_permissions,
              adhoc_tasks_enabled: p.editingMember.adhoc_tasks_enabled ?? true,
              instant_task_scope: p.editingMember.instant_task_scope ?? 'all',
              instant_task_members: p.editingMember.instant_task_members ?? [],
            } : undefined}
            // Only members with instant task access (View Instant Tasks, or admin's auto-grant) can be selected.
            memberOptions={p.members.filter((member) =>
              member.id !== p.editingMember?.id &&
              (member.org_permissions === 'admin' || (member.feature_permissions?.workflows ?? []).includes('view_instant'))
            )}
            onSubmit={p.editingMember ? p.handleUpdateMember : p.handleAddMember}
          />
          <ConfirmDialog
            isOpen={!!p.deletingMember}
            onClose={() => p.setDeletingMember(null)}
            onConfirm={p.handleDeleteMember}
            title="Delete Team Member"
            message={`Are you sure you want to remove ${p.deletingMember?.name}? This action cannot be undone.`}
            confirmText="Delete" colorScheme="red"
          />
          {p.canImportTeam && (
            <ImportUsersDialog
              isOpen={p.isImportDialogOpen}
              onClose={() => p.setIsImportDialogOpen(false)}
              onImport={p.handleImportUsers}
              isLoading={p.isImporting}
            />
          )}
        </>
      )}
    </>
  );
}
